/* eslint-disable max-lines-per-function */
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast
} from "@chakra-ui/react"
import { gql, useLazyQuery, useMutation } from "@apollo/client"
import { useAtom } from "jotai"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import { userDetailsAtom, userProfileDataAtom } from "../../Menu/menu.store"
import { clearAuthData } from "../../Menu/menu.support"
import { failForm, UPDATE_USERPROFILE } from "../../../pages/login/signupNew/signup.support"

const GETUSER_PROFILE = gql`
  query ViewProfile($userId: Int!) {
    viewProfile(user_id: $userId) {
      user_id
      first_name
      last_name
      email_address
      company_name
      address1
      address2
      suburb
      city
      state
      pincode
      company_logo
      supplier
    }
  }
`

const DELETE_ACCOUNT = gql`
  mutation DeleteProfile($userId: Int!) {
    deleteProfile(user_id: $userId)
  }
`

const SUPPLIER_GROUPS = [
  {
    label: "Preferred Partners",
    options: ["Acura", "Drillcut", "Drillfast", "PowAGroup"]
  },
  {
    label: "Other Partners",
    options: ["Cooks Plumbing", "Fyreguard", "Global Fasteners", "Plumbers Co-op", "Reece", "Tradelink", "Yousta", "Other"]
  }
]

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"]

interface ProfileModalProps {
  isOpen: boolean
  onClose: () => void
}

const inputStyle = {
  borderRadius: "8px",
  height: "40px",
  border: "1px solid #ddd",
  fontSize: "14px",
  _focus: { borderColor: "#861F41", boxShadow: "0 0 0 1px #861F41" }
}

const labelStyle = {
  fontSize: "13px",
  fontWeight: "500" as const,
  color: "gray.700",
  mb: "1"
}

const getUserInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.[0]?.toUpperCase() ?? ""
  const l = lastName?.[0]?.toUpperCase() ?? ""
  return f + l || "?"
}

export const ProfileModal = ({ isOpen, onClose }: ProfileModalProps): JSX.Element => {
  const toast = useToast()
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom)
  const [, setUserProfileData] = useAtom(userProfileDataAtom)

  // Profile form state
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [address1, setAddress1] = useState("")
  const [address2, setAddress2] = useState("")
  const [suburb, setSuburb] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [postcode, setPostcode] = useState("")
  const [supplier, setSupplier] = useState("")
  const [logoFileName, setLogoFileName] = useState("")
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("")
  const [isDragging, setIsDragging] = useState(false)

  // Password tab state
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [passwordError, setPasswordError] = useState("")

  const [fetchProfile, { data: profileData, loading: profileLoading }] = useLazyQuery(GETUSER_PROFILE, {
    fetchPolicy: "network-only"
  })
  const [updateProfile, { data: updateData, loading: updateLoading, error: updateError }] = useMutation(UPDATE_USERPROFILE)
  const [deleteAccount, { data: deleteData, loading: deleteLoading }] = useMutation(DELETE_ACCOUNT)

  // Load profile on open
  useEffect(() => {
    if (isOpen && userDetails?._id) {
      fetchProfile({ variables: { userId: userDetails._id } }).catch(() => {})
    }
  }, [isOpen, userDetails?._id])

  // Populate fields from fetched profile
  useEffect(() => {
    if (!profileData?.viewProfile) return
    const p = profileData.viewProfile
    setFirstName(p.first_name ?? "")
    setLastName(p.last_name ?? "")
    setEmail(p.email_address ?? "")
    setCompany(p.company_name ?? "")
    setAddress1(p.address1 ?? "")
    setAddress2(p.address2 ?? "")
    setSuburb(p.suburb ?? "")
    setCity(p.city ?? "")
    setState(p.state ?? "")
    setPostcode(p.pincode ?? "")
    setSupplier(p.supplier ?? "")
    setLogoFileName(p.company_logo ?? "")
    const logo = p.company_logo ?? ""
    setLogoPreviewUrl(logo.startsWith("http") || logo.startsWith("/") ? logo : "")
  }, [profileData])

  // Handle update success
  useEffect(() => {
    if (updateData?.updateProfile === "success") {
      toast({ title: "Saved", status: "success", duration: 2000, isClosable: true })
      if (newPassword && currentPassword) {
        alert("Password changed. You will be logged out. Please log in with your new credentials.")
        clearAuthData()
        setUserDetails(null)
        setUserProfileData({})
        onClose()
        navigate("/")
      }
    }
    if (updateError) {
      failForm(updateError.graphQLErrors[0] as unknown as string, toast)
    }
  }, [updateData, updateError])

  // Handle account deletion
  useEffect(() => {
    if (deleteData?.deleteProfile === "success") {
      toast({ title: "Account deleted", status: "success", duration: 2000, isClosable: true })
      setTimeout(() => navigate(Paths.LOGIN.logout), 2000)
    }
  }, [deleteData])

  const handleProfileSave = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!userDetails?._id) return
    const currentEmail = profileData?.viewProfile?.email_address ?? ""
    const vars: Record<string, any> = {
      user_id: userDetails._id,
      first_name: firstName,
      last_name: lastName,
      email_address: currentEmail,
      company_name: company
    }
    // Only send new_email_address if the user actually changed it
    if (email !== currentEmail) vars.new_email_address = email
    if (address1) vars.address1 = address1
    if (address2) vars.address2 = address2
    if (suburb) vars.suburb = suburb
    if (city) vars.city = city
    if (state) vars.state = state
    if (postcode) vars.pincode = postcode
    if (supplier) vars.supplier = supplier
    if (logoFileName) vars.company_logo = logoFileName
    updateProfile({ variables: vars }).catch(() => {})
  }

  const handlePasswordUpdate = (e: React.FormEvent): void => {
    e.preventDefault()
    setPasswordError("")
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.")
      return
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters.")
      return
    }
    if (!userDetails?._id) return
    updateProfile({
      variables: {
        user_id: userDetails._id,
        email_address: profileData?.viewProfile?.email_address,
        password: currentPassword,
        new_password: newPassword
      }
    }).catch(() => {})
  }

  const handleDeleteAccount = (): void => {
    if (!window.confirm("Are you sure you want to delete your account? This cannot be undone.")) return
    if (!userDetails?._id) return
    deleteAccount({ variables: { userId: userDetails._id } }).catch(() => {})
  }

  const handleFileDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      setLogoFileName(file.name)
      setLogoPreviewUrl(URL.createObjectURL(file))
    }
  }

  const initials = getUserInitials(userDetails?.first_name, userDetails?.last_name)

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="rgba(0,0,0,0.35)" backdropFilter="blur(8px)" />
      <ModalContent borderRadius="16px" maxW="440px" overflow="hidden" mx="4">
        {/* Header */}
        <Box bg="#861F41" px="6" py="5" position="relative">
          <Flex align="center" gap="3">
            <Flex
              w="48px" h="48px" borderRadius="full" bg="rgba(255,255,255,0.2)"
              align="center" justify="center" flexShrink={0}
            >
              <Text color="white" fontWeight="700" fontSize="16px">{initials}</Text>
            </Flex>
            <Box>
              <Text color="white" fontWeight="600" fontSize="15px">
                {userDetails?.first_name} {userDetails?.last_name}
              </Text>
              <Text color="rgba(255,255,255,0.8)" fontSize="13px">{userDetails?.email_address}</Text>
            </Box>
          </Flex>
          <Button
            position="absolute" top="3" right="3" variant="ghost" color="white"
            _hover={{ bg: "rgba(255,255,255,0.15)" }} size="sm" onClick={onClose} aria-label="Close"
          >
            ✕
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs colorScheme="red" isLazy>
          <TabList px="6" borderBottomColor="#f0f0f0">
            <Tab fontSize="14px" fontWeight="500" _selected={{ color: "#861F41", borderBottomColor: "#861F41" }}>
              My Profile
            </Tab>
            <Tab fontSize="14px" fontWeight="500" _selected={{ color: "#861F41", borderBottomColor: "#861F41" }}>
              Change Password
            </Tab>
          </TabList>

          <TabPanels maxH="60vh" overflowY="auto">
            {/* ── My Profile Tab ── */}
            <TabPanel px="6" py="5">
              <form onSubmit={handleProfileSave}>
                <Text fontSize="13px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="3">
                  Required
                </Text>
                <Grid templateColumns="1fr 1fr" gap="3" mb="3">
                  <Box>
                    <Text {...labelStyle}>First name</Text>
                    <Input {...inputStyle} value={firstName} onChange={e => setFirstName(e.target.value)} required />
                  </Box>
                  <Box>
                    <Text {...labelStyle}>Last name</Text>
                    <Input {...inputStyle} value={lastName} onChange={e => setLastName(e.target.value)} required />
                  </Box>
                </Grid>
                <Box mb="3">
                  <Text {...labelStyle}>Email address</Text>
                  <Input {...inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                </Box>
                <Box mb="4">
                  <Text {...labelStyle}>Company name</Text>
                  <Input {...inputStyle} value={company} onChange={e => setCompany(e.target.value)} required />
                </Box>

                <Divider mb="4" />

                <Text fontSize="13px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="3">
                  Optional
                </Text>
                <Box mb="3">
                  <Text {...labelStyle}>Address line 1</Text>
                  <Input {...inputStyle} value={address1} onChange={e => setAddress1(e.target.value)} />
                </Box>
                <Box mb="3">
                  <Text {...labelStyle}>Address line 2</Text>
                  <Input {...inputStyle} value={address2} onChange={e => setAddress2(e.target.value)} />
                </Box>
                <Grid templateColumns="1fr 1fr" gap="3" mb="3">
                  <Box>
                    <Text {...labelStyle}>Suburb</Text>
                    <Input {...inputStyle} value={suburb} onChange={e => setSuburb(e.target.value)} />
                  </Box>
                  <Box>
                    <Text {...labelStyle}>City</Text>
                    <Input {...inputStyle} value={city} onChange={e => setCity(e.target.value)} />
                  </Box>
                </Grid>
                <Grid templateColumns="1fr 1fr" gap="3" mb="3">
                  <Box>
                    <Text {...labelStyle}>State</Text>
                    <Select
                      {...inputStyle}
                      value={state}
                      onChange={e => setState(e.target.value)}
                      placeholder="Select state"
                    >
                      {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  </Box>
                  <Box>
                    <Text {...labelStyle}>Postcode</Text>
                    <Input {...inputStyle} value={postcode} onChange={e => setPostcode(e.target.value)} maxLength={4} />
                  </Box>
                </Grid>
                <Box mb="3">
                  <Text {...labelStyle}>Company logo</Text>
                  <Box
                    border={`2px dashed ${isDragging ? "#861F41" : "#ddd"}`}
                    borderRadius="8px"
                    p="4"
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragging ? "red.50" : "gray.50"}
                    onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleFileDrop}
                    onClick={() => document.getElementById("logo-file-input")?.click()}
                  >
                    <Text fontSize="13px" color="gray.500">
                      {logoFileName || "Drop file here or browse"}
                    </Text>
                    <input
                      id="logo-file-input"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={e => {
                        const f = e.target.files?.[0]
                        if (f) {
                          setLogoFileName(f.name)
                          setLogoPreviewUrl(URL.createObjectURL(f))
                        }
                      }}
                    />
                  </Box>
                  {logoPreviewUrl && (
                    <Box mt="2" p="3" border="1px solid #eee" borderRadius="8px" bg="white" textAlign="center">
                      <img
                        src={logoPreviewUrl}
                        alt="Company logo preview"
                        style={{ maxHeight: "72px", maxWidth: "100%", objectFit: "contain", margin: "0 auto", display: "block" }}
                      />
                    </Box>
                  )}
                </Box>
                <Box mb="5">
                  <Text {...labelStyle}>Supplier name</Text>
                  <Select {...inputStyle} value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Select supplier">
                    {SUPPLIER_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </optgroup>
                    ))}
                  </Select>
                </Box>

                <Button
                  type="submit"
                  w="100%"
                  bg="#861F41"
                  color="white"
                  borderRadius="24px"
                  height="42px"
                  fontWeight="600"
                  isLoading={updateLoading || profileLoading}
                  _hover={{ bg: "#6d1934" }}
                >
                  Save Changes
                </Button>
              </form>
            </TabPanel>

            {/* ── Change Password Tab ── */}
            <TabPanel px="6" py="5">
              <form onSubmit={handlePasswordUpdate}>
                <Box mb="3">
                  <Text {...labelStyle}>Current password</Text>
                  <InputGroup>
                    <Input {...inputStyle} type={showCurrent ? "text" : "password"} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required pr="10" />
                    <InputRightElement h="40px">
                      <Button size="xs" variant="ghost" color="gray.500" onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>
                        {showCurrent ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <Text {...labelStyle}>New password</Text>
                  <InputGroup>
                    <Input {...inputStyle} type={showNew ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} required pr="10" />
                    <InputRightElement h="40px">
                      <Button size="xs" variant="ghost" color="gray.500" onClick={() => setShowNew(v => !v)} tabIndex={-1}>
                        {showNew ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <Box mb="4">
                  <Text {...labelStyle}>Confirm new password</Text>
                  <InputGroup>
                    <Input {...inputStyle} type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required pr="10" />
                    <InputRightElement h="40px">
                      <Button size="xs" variant="ghost" color="gray.500" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                        {showConfirm ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </Box>

                {passwordError && (
                  <Text color="red.500" fontSize="13px" mb="3">{passwordError}</Text>
                )}

                <Button
                  type="submit"
                  w="100%"
                  bg="#861F41"
                  color="white"
                  borderRadius="24px"
                  height="42px"
                  fontWeight="600"
                  isLoading={updateLoading}
                  _hover={{ bg: "#6d1934" }}
                >
                  Update Password
                </Button>
              </form>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Footer — delete account */}
        <Box borderTop="1px solid #f0f0f0" px="6" py="4" textAlign="center">
          <Text
            as="span"
            color="red.500"
            fontSize="13px"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={deleteLoading ? undefined : handleDeleteAccount}
            opacity={deleteLoading ? 0.5 : 1}
          >
            {deleteLoading ? "Deleting..." : "Delete my account"}
          </Text>
        </Box>
      </ModalContent>
    </Modal>
  )
}
